import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { todayLocal } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { MonthRevenueChart, OccupancyMonthChart } from "@/pages/reports/reportCharts";
import { DeltaBadge, StatCard, formatPercent } from "@/pages/reports/reportShared";
import type { YearlyMonthRevenue, YearlyReport } from "@/types/camping";

function sumThrough(months: YearlyMonthRevenue[], throughMonth: number): number {
  return months
    .filter((month) => month.month <= throughMonth)
    .reduce((sum, month) => sum + (Number.parseFloat(month.invoiced_total) || 0), 0);
}

function monthName(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString("de-DE", { month: "long" });
}

export function SeasonSnapshot({ onOpenDevelopment }: { onOpenDevelopment: () => void }) {
  const today = todayLocal();
  const year = Number.parseInt(today.slice(0, 4), 10);
  const throughMonth = Number.parseInt(today.slice(5, 7), 10);
  const [report, setReport] = useState<YearlyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<YearlyReport>(`/api/v1/reports/yearly?year=${year}`)
      .then(setReport)
      .catch((err: unknown) => {
        setReport(null);
        setError(err instanceof Error ? err.message : "Saisonverlauf konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const currency = report?.currency ?? "EUR";
  const monthLabel = monthName(year, throughMonth);

  const ytd = useMemo(() => {
    if (!report) return null;
    const current = sumThrough(report.months, throughMonth);
    const prior = report.prior_year ? sumThrough(report.prior_year.months, throughMonth) : null;
    const currentMonth = report.months.find((month) => month.month === throughMonth);
    return { current, prior, currentMonth };
  }, [report, throughMonth]);

  return (
    <section className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-label">Saison {year}</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Umsatz und Auslastung im Jahresverlauf, der Umsatz bis {monthLabel} gegen denselben Zeitraum{" "}
            {year - 1}.
          </p>
        </div>
        <button type="button" className="eh-btn eh-btn--secondary min-h-[44px]" onClick={onOpenDevelopment}>
          Entwicklung öffnen
        </button>
      </header>

      {error ? (
        <p className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Saisonverlauf wird geladen…</p>
      ) : report && ytd ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <StatCard
              label={`Umsatz bis ${monthLabel}`}
              value={formatCurrency(ytd.current, currency)}
              delta={
                ytd.prior != null ? <DeltaBadge current={ytd.current} prior={ytd.prior} /> : undefined
              }
              hint={
                ytd.prior != null
                  ? `${year - 1} im gleichen Zeitraum: ${formatCurrency(ytd.prior, currency)}`
                  : "Nach Rechnungsdatum, Januar bis zum aktuellen Monat."
              }
            />
            <StatCard
              label={`Auslastung ${monthLabel}`}
              value={formatPercent(ytd.currentMonth?.occupancy_rate_percent ?? null)}
              hint="Belegte Platznächte im laufenden Monat."
            />
          </div>

          <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
            <h3 className="font-medium text-label">Umsatz je Monat</h3>
            <div className="mt-4">
              <MonthRevenueChart
                months={report.months}
                priorMonths={report.prior_year?.months ?? null}
                currency={currency}
                currentYearLabel={String(report.year)}
                priorYearLabel={report.prior_year ? String(report.prior_year.year) : undefined}
                highlightMonth={throughMonth}
              />
            </div>
          </section>

          <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
            <h3 className="font-medium text-label">Auslastung je Monat</h3>
            <div className="mt-4">
              <OccupancyMonthChart months={report.months} />
            </div>
          </section>
        </>
      ) : null}
    </section>
  );
}
